import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { MapPin, Navigation, Settings, X, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { useAuth } from '../contexts/AuthContext';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';
import { alwaysLocationService } from '../services/alwaysLocationService';
import { isInternalDriverUser } from '../utils/userRoles';

const DRIVER_ROLES = new Set([
  'external-driver',
  'internal-driver',
  'corporate-external-transport-driver',
  'driver',
  'external-transport-driver',
]);

const storePosition = (position: GeolocationPosition) => {
  localStorage.setItem('driverLocation', JSON.stringify({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    timestamp: new Date().toISOString(),
  }));
};

const requestBrowserPosition = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve(false);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        storePosition(position);
        resolve(true);
      },
      () => resolve(false),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 }
    );
  });

export function DriverLocationPermissionCheck() {
  const { user: authUser } = useAuth();
  const checkedOnceRef = useRef(false);
  const [showAlwaysPrompt, setShowAlwaysPrompt] = useState(false);
  const [busyAction, setBusyAction] = useState<'request' | 'settings' | null>(null);

  const openAppSettings = useCallback(async () => {
    const result = await alwaysLocationService.openSettingsIfAvailable();
    if (!result && Capacitor.isNativePlatform()) {
      toast.error('Cihaz ayarları açılamadı');
    }
  }, []);

  const handleOpenSettings = useCallback(async () => {
    if (busyAction) return;
    setBusyAction('settings');
    try {
      if (!Capacitor.isNativePlatform()) {
        const granted = await requestBrowserPosition();
        if (granted) {
          setShowAlwaysPrompt(false);
          toast.success('Konum izniniz alındı');
        } else {
          toast.error('Konum izni verilmeden devam edilemiyor');
        }
        return;
      }
      await openAppSettings();
    } finally {
      setBusyAction(null);
    }
  }, [busyAction, openAppSettings]);

  const handleRetry = useCallback(async () => {
    if (busyAction) return;
    setBusyAction('request');
    try {
      if (!Capacitor.isNativePlatform()) {
        const granted = await requestBrowserPosition();
        if (granted) {
          setShowAlwaysPrompt(false);
          toast.success('Konum izniniz alındı, artık takip edilebilirsiniz');
        } else {
          toast.error('Konum izni verilmeden devam edilemiyor');
        }
        return;
      }
      const permission = await alwaysLocationService.requestAlwaysIfAvailable();
      if (permission?.status === 'authorizedAlways') {
        setShowAlwaysPrompt(false);
        toast.success('Konum izniniz alındı, artık takip edilebilirsiniz');
        return;
      }
      toast.info('İzin verilmedi veya değiştirilmek istendi. Konum ayarlarına yönlendiriliyorsunuz.');
      await openAppSettings();
    } finally {
      setBusyAction(null);
    }
  }, [busyAction, openAppSettings]);

  useEffect(() => {
    if (checkedOnceRef.current) return;
    const delay = window.setTimeout(() => {
      checkedOnceRef.current = true;
      const user = getStoredDriverMobileUser<any>() || authUser;
      if (!user?.id) return;
      if (isInternalDriverUser(user)) return;
      if (isCorporateExternalTransportMobileUser(user)) return;
      if (!DRIVER_ROLES.has(String(user?.role || '')) && user?.driverType !== 'external' && user?.driverType !== 'internal') return;

      const runCheck = async () => {
        if (Capacitor.isNativePlatform()) {
          const status = await alwaysLocationService.checkStatusIfAvailable();
          if (status && status.status !== 'authorizedAlways') {
            setShowAlwaysPrompt(true);
          }
          return;
        }
        const granted = await requestBrowserPosition();
        if (!granted) {
          setShowAlwaysPrompt(true);
        }
      };
      void runCheck();
    }, 600);

    return () => window.clearTimeout(delay);
  }, [authUser]);

  if (!showAlwaysPrompt) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Konum izni gerekli"
      className="fixed inset-0 z-[1000000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busyAction) setShowAlwaysPrompt(false);
      }}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-[#161616]">
        <div className="relative bg-gradient-to-br from-emerald-500 to-teal-600 px-5 pb-5 pt-6 text-white">
          <button
            type="button"
            onClick={() => setShowAlwaysPrompt(false)}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30 active:scale-95"
            aria-label="Kapat"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/20">
              <MapPin className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-100">Konum İzni Gerekli</p>
              <h2 className="text-lg font-bold leading-snug">Yolculuğunuz Boyunca Yanınızdayız! 📍</h2>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
            Ekranınız kapalıyken bile size yeni işler bulabilmemiz ve süreçlerinizi hızlandırmamız için konum izninize
            ihtiyacımız var.
          </p>
          <p className="text-sm font-medium leading-6 text-slate-700 dark:text-slate-200">
            Konumunuzu <span className="font-bold text-emerald-600 dark:text-emerald-400">&quot;Her Zaman&quot;</span> açık
            tutarak:
          </p>
          <ul className="space-y-2.5 text-sm leading-5 text-slate-600 dark:text-slate-300">
            <li className="flex items-start gap-2.5">
              <span className="text-base leading-5">🎯</span>
              <span>Tesislerdeki anlık işleri kaçırmazsınız.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-base leading-5">⏱️</span>
              <span>Otomatik yönlendirmelerle sıra beklemeden hız kazanırsınız.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-base leading-5">💰</span>
              <span>Hakedişlerinizin eksiksiz hesaplanmasını sağlarsınız.</span>
            </li>
          </ul>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs leading-5 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
            Lütfen bir sonraki ekranda <span className="font-bold">&quot;Her zaman izin ver&quot;</span> seçeneğini seçin!
          </div>
        </div>

        <div className="space-y-2.5 border-t border-slate-100 px-5 py-4 dark:border-slate-800">
          <button
            type="button"
            disabled={busyAction !== null}
            onClick={() => void handleOpenSettings()}
            className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-base font-bold text-white shadow-sm transition active:scale-[0.98] disabled:opacity-60 dark:bg-emerald-500 dark:hover:bg-emerald-400"
          >
            {busyAction === 'settings' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Settings className="h-5 w-5" />}
            Konum Ayarlarını Aç
          </button>
          <button
            type="button"
            disabled={busyAction !== null}
            onClick={() => void handleRetry()}
            className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition active:scale-[0.98] disabled:opacity-60 dark:border-slate-600 dark:bg-transparent dark:text-slate-200"
          >
            {busyAction === 'request' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Navigation className="h-5 w-5" />}
            Tekrar Dene
          </button>
          <button
            type="button"
            disabled={busyAction !== null}
            onClick={() => setShowAlwaysPrompt(false)}
            className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold text-slate-500 transition active:scale-[0.98] disabled:opacity-60 dark:text-slate-400"
          >
            <CheckCircle2 className="h-5 w-5" />
            Daha Sonra
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}